import { Col, Row } from 'react-grid-system'
import styled from 'styled-components'
import textsData from '@/textData/ContractDetailTexts.json'
import { useContractDetailsContext, useErrorHandlerContext, useUpdateContractFormModalContext, useUserLanguage } from '@/customHooks';
import { ContractDetail } from '@/custom-types';
import { advancedNormalizeDate, capitalizeFirstLetter, contractLeaseTermMapper, contractStatusMapper, normalizeDate } from '@/helpers';
import { Button, RowSpacer, ToggleSwitch } from '../atoms';
import {
  NormalFormNumberField,
  RectangleInfoCard,
  SmallInfoCard,
  SmallInfoCardProps,
  StatsCard,
  StatsCardProps,
} from '../molecules';

import leaseStatusIcon from '@/assets/contract-icons/lease-status-icon.svg'
import monthlyRentIcon from '@/assets/contract-icons/montly-rent-icon.svg'
import depositHeldIcon from '@/assets/contract-icons/deposits-held-icon.svg'
import insuranceIcon from '@/assets/contract-icons/insurance-icon.svg'
import balanceIcon from '@/assets/contract-icons/balance.svg'
import fixedTerm from '@/assets/contract-icons/fixed-term-icon.svg'
import linkedUnits from '@/assets/contract-icons/units-icon.svg'
import { UpdateContractFormModal } from '../templates';
import PersonIcon from '@/assets/contract-icons/person.svg'
import confirmIcon from '@/assets/contract-icons/confirm-icon.svg'

const Subtitle = styled.p`
  font-size: 0.9rem;
  color: #666;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  margin: 24px 0 12px;
`;

const CardButtonWrapper = styled.div`
  background: none;
  border: none;
  cursor: pointer;
`

const KeysButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 9999px;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;

  &:hover:enabled {
    background-color: #f5f5f5;
    box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  }

  &:active:enabled {
    transform: translateY(1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const KeysIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const KeysText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
`;

const KeysDate = styled.span`
  margin-left: auto;
  font-size: 0.85rem;
  color: #666;
`;

/* TODO: fecha: 260130 -> Componente provisional, se debe retirar despues de haber sido verificada la informacion de contratos por parte de los usuarios */
import { useEffect, useState } from 'react';
import { useUpdateContractMutation } from '@/graphql/generated';

const StyledSmallInfoCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`
const MonthlyAmountCard = () => {
  const [newAmount, setNewAmount] = useState('0')
  const [isAutoGenerateRentCharges, setIsAutoGenerateRentCharges] = useState(false)
  const [buttonDisabled, setButtonDisabled] = useState(true)
  const [updateConfrimation, setUpdateConfrimation] = useState(false)

  const [updateContract, { loading }] = useUpdateContractMutation()
  const { contract, setContract } = useContractDetailsContext()

  const { open: errorHandle } = useErrorHandlerContext()

  useEffect(() => {
    if (newAmount === '0') setButtonDisabled(true)
    else setButtonDisabled(false)
  }, [newAmount])

  useEffect(() => {
    if (isAutoGenerateRentCharges !== contract?.isAutoGenerateRentCharges) setButtonDisabled(false)
    else setButtonDisabled(true)
  }, [isAutoGenerateRentCharges])

  useEffect(() => {
    if (contract) {
      setIsAutoGenerateRentCharges(contract.isAutoGenerateRentCharges)
      setNewAmount(contract.monthlyChargeAmount?.toString() ?? '0')
    }
  }, [contract])

  const onClickHandle = () => {
    updateContract({
      variables: {
        contractId: contract?.id ?? '',
        input: {
          monthlyChargeAmount: newAmount,
          isAutoGenerateRentCharges: isAutoGenerateRentCharges,
        },
      },
    })
      .then(() => {
        setUpdateConfrimation(true)
        setContract(undefined)
      })
      .catch(errorHandle)
  }

  return (
    <StyledSmallInfoCard>
      <Row>
        <Col>
          <h2>Monto mensual</h2>
        </Col>
      </Row>
      <br />
      <Row>
        <Col>
          <span>Habilitar cargo mensual automático</span>
        </Col>
        <Col>
          <ToggleSwitch
            checked={contract?.isAutoGenerateRentCharges ?? false}
            onChange={(value) => setIsAutoGenerateRentCharges(value)}
          />
        </Col>
      </Row>
      <Row justify="between" align="center" gutterWidth={32}>
        <Col>
          <NormalFormNumberField
            label="Nuevo monto"
            name="monthlyChargeAmount"
            value={contract?.monthlyChargeAmount ? contract.monthlyChargeAmount.toString() : '0'}
            onChange={(value) => setNewAmount(value ?? '0')}
          />
        </Col>
        <Col xs='content'>
          <Button
            onClick={onClickHandle}
            disabled={buttonDisabled}
            isLoading={loading}
          >
            Actualizar
          </Button>
        </Col>
      </Row>
      <br />
      <Row>
        <Col>
          {updateConfrimation && (
            <p>Monto mensual actualizado con éxito, por favor actualiza la página para ver los cambios</p>
          )}
        </Col>
      </Row>
    </StyledSmallInfoCard>
  )
}

const buildPropertyAddress = (property: ContractDetail['property']): string => {
  if (!property || !property.address) return 'No data'

  const { address } = property
  const streetParts = [
    address.street,
    address.buildingNumber,
    address.addressLine,
  ].filter(Boolean)

  const locationParts = [
    address.city?.name,
    address.state?.name,
    address.country?.name,
    address.zipCode,
  ].filter(Boolean)

  const streetText = streetParts.join(' ')
  const locationText = locationParts.join(', ')

  if (streetText && locationText) return `${streetText} - ${locationText}`
  if (streetText) return streetText
  if (locationText) return locationText

  return 'No data'
}

export const ContractDetailOverview = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.overview : textsData.en.overview
  const propertySectionTexts = texts.propertySection

  const { openModal: openUpdateContractModal } = useUpdateContractFormModalContext()

  const { contract, setContract } = useContractDetailsContext()
  const { open: errorHandle } = useErrorHandlerContext()
  const [updateContract] = useUpdateContractMutation()

  const formatKeyDate = (isoDate?: string | null) => {
    if (!isoDate) return userLanguage === 'es' ? 'Sin fecha' : 'No date'
    try {
      const d = new Date(isoDate)
      return d.toLocaleDateString(
        userLanguage === 'es' ? 'es-ES' : 'en-US',
        { year: 'numeric', month: 'short', day: 'numeric' }
      )
    } catch {
      return userLanguage === 'es' ? 'Sin fecha' : 'No date'
    }
  }

  const handleKeysUpdate = async (field: 'keysDelivered' | 'keysReceived') => {
    if (!contract?.id) return

    if (field === 'keysReceived' && !contract.keysDelivered) {
      return
    }

    const nowLocal = new Date()
    const nowUtcIso = new Date(
      Date.UTC(
        nowLocal.getFullYear(),
        nowLocal.getMonth(),
        nowLocal.getDate(),
        nowLocal.getHours(),
        nowLocal.getMinutes(),
        nowLocal.getSeconds(),
        nowLocal.getMilliseconds()
      )
    ).toISOString()

    try {
      await updateContract({
        variables: {
          contractId: contract.id,
          input: {
            [field]: nowUtcIso,
          },
        },
      })
      setContract({
        ...contract,
        [field]: nowUtcIso,
      })
    } catch (e) {
      errorHandle(e as Error)
    }
  }

  const infoCards: StatsCardProps[] = [
    {
      label: texts.infoCards.card1,
      value: 0,
      iconSrc: balanceIcon,
    },
    {
      label: contract ? contractLeaseTermMapper(contract.leaseTermOption, userLanguage) : 'No Data',
      value: contract ? `${normalizeDate(contract.startDate)} - ${normalizeDate(contract.finishDate)}` : 'No data',
      iconSrc: fixedTerm,
    },
    {
      label: texts.infoCards.card4,
      value: contract ? contract.monthlyChargeAmount?.toString() ?? 'No data' : 'No data',
      iconSrc: balanceIcon,
    },
  ]

  const contractedUnities = contract?.unities.map((unity) => {
    return {
      label: texts.infoCards.card3,
      value: capitalizeFirstLetter(unity.name),
      iconSrc: linkedUnits
    }
  }) ?? []

  const smallInfoCardData: SmallInfoCardProps[] = [
    {
      title: texts.minicards.card1,
      value: contract ? contractStatusMapper(contract.statusOption, userLanguage) : 'No data',
      icon: leaseStatusIcon,
    },
    {
      title: texts.minicards.card3,
      value: contract?.rentAmount ?? 'No Data',
      icon: monthlyRentIcon,
    },
    {
      title: texts.minicards.card2,
      value: 'No Data',
      icon: depositHeldIcon,
    },
    {
      title: texts.minicards.card4,
      value: '+ Add insurance',
      icon: insuranceIcon,
    },
  ]

  if (!contract) return (
    <Row>
      <Col>
        <span><b>{texts.noData}</b></span>
      </Col>
    </Row>
  )

  return (
    <>
      <div style={{ padding: '24px' }}>
        <Row justify="between" align="center">
          <Col>
            <h1>{texts.title}</h1>
            <Subtitle>{`${texts.description} ${contract.id}`}</Subtitle>
            <Subtitle>{`${texts.finishDate} ${advancedNormalizeDate(userLanguage, contract.finishDate)}`}</Subtitle>
          </Col>
          <Col>
            <Row gutterWidth={16} justify="end">
              <Col xs='content'>
                <Button
                  onClick={() => openUpdateContractModal()}
                  secondary
                >
                  {texts.buttons.button2}
                </Button>
              </Col>
              <Col xs='content'>
                <Button
                  onClick={() => { }}
                >
                  {texts.buttons.button1}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <RowSpacer height={3} />
        <SectionTitle>{userLanguage === 'es' ? 'Llaves' : 'Keys'}</SectionTitle>
        <br />
        <Row gutterWidth={24}>
          <Col md={6}>
            <KeysButton
              type="button"
              onClick={() => handleKeysUpdate('keysDelivered')}
            >
              <KeysIcon src={confirmIcon} alt="Keys delivered" />
              <KeysText>{userLanguage === 'es' ? 'Llaves entregadas' : 'Keys delivered'}</KeysText>
              <KeysDate>{formatKeyDate(contract.keysDelivered)}</KeysDate>
            </KeysButton>
          </Col>
          <Col md={6}>
            <KeysButton
              type="button"
              onClick={() => handleKeysUpdate('keysReceived')}
              disabled={!contract.keysDelivered}
            >
              <KeysIcon src={confirmIcon} alt="Keys received" />
              <KeysText>{userLanguage === 'es' ? 'Llaves recibidas' : 'Keys received'}</KeysText>
              <KeysDate>{formatKeyDate(contract.keysReceived)}</KeysDate>
            </KeysButton>
          </Col>
        </Row>
        <RowSpacer height={3} />
        {/*TODO: Fecha 260130, Este boton se coloca solo de forma provisoria, retirar posteriormente */}
        <Row>
          <Col>
            <MonthlyAmountCard />
          </Col>
        </Row>
        <RowSpacer height={3} />
        <SectionTitle>{texts.sectionsTitles.associatedProperty}</SectionTitle>
        <br />
        <Row>
          <Col md={12}>
            <RectangleInfoCard
              title={
                contract.property
                  ? contract.property.name
                  : propertySectionTexts.noPropertyTitle
              }
              subtitle={
                contract.property
                  ? buildPropertyAddress(contract.property)
                  : propertySectionTexts.noPropertySubtitle
              }
              iconSrc={linkedUnits}
            />
          </Col>
        </Row>
        <RowSpacer height={3} />
        {/* Fin del componente ***********************/}
        <Row>
          {
            infoCards.map((card, index) => (
              <Col md={4} key={card.label + index}>
                <StatsCard
                  label={card.label}
                  value={card.value}
                  iconSrc={card.iconSrc}
                />
              </Col>
            ))
          }
        </Row>
        <RowSpacer height={3} />
        <Row>
          {
            contractedUnities.map((card, index) => (
              <Col md={4} key={card.label + index}>
                <StatsCard
                  label={card.label}
                  value={card.value}
                  iconSrc={card.iconSrc}
                />
              </Col>
            ))
          }
        </Row>
        <RowSpacer height={3} />
        <Row>
          <Col xs={12}>
            <SectionTitle>{texts.sectionsTitles.section1}</SectionTitle>
          </Col>
          <Col xs={12}>
            <Row gutterWidth={48} style={{ marginTop: '24px' }}>
              {
                smallInfoCardData.map((data, index) => (
                  <Col key={data.title + index}>
                    <SmallInfoCard
                      title={data.title}
                      value={data.value}
                      icon={data.icon}
                    />
                  </Col>
                ))
              }
            </Row>
          </Col>
        </Row>
        <RowSpacer height={3} />
        <SectionTitle>{texts.participantsSection.title}</SectionTitle>
        <br />
        <Row gutterWidth={48}>
          <Col md={6}>
            <CardButtonWrapper
              onClick={() => { }}
            >
              <RectangleInfoCard
                title={texts.participantsSection.card1.title}
                subtitle={
                  contract.owner
                    ? `${capitalizeFirstLetter(contract.owner?.firstName)} ${capitalizeFirstLetter(contract.owner?.lastName)}`
                    : texts.participantsSection.card1.noData
                }
                iconSrc={PersonIcon}
              />
            </CardButtonWrapper>
          </Col>
          <Col md={6}>
            <CardButtonWrapper
              onClick={() => { }}
            >
              <RectangleInfoCard
                title={texts.participantsSection.card2.title}
                subtitle={
                  contract.tenant
                    ? `${capitalizeFirstLetter(contract.tenant?.firstName)} ${capitalizeFirstLetter(contract.tenant?.lastName)}`
                    : texts.participantsSection.card2.noData
                }
                iconSrc={PersonIcon}
              />
            </CardButtonWrapper>
          </Col>
        </Row>
      </div>
      <UpdateContractFormModal />
    </>
  )
}